import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface Tariff {
  id: number;
  from: string | null;
  to: string | null;
  transport_type: {
    id: number;
    name: string;
  };
  price: number;
  origin_port: {
    id: number;
    name: string;
    country: string | null;
  };
  destination_port: {
    id: number;
    name: string;
    country: string | null;
  };
  bookings_type: {
    id: number;
    name: string;
  } | null;
  tariff_bracket: {
    id: number;
    from: number | null;
    to: number | null;
  } | null;
  container_size: {
    id: number;
    name: string;
  } | null;
}

interface RawTariffResponse {
  id: number;
  from: string | null;
  to: string | null;
  price: number;
  transport_type: number;
  origin_port: number;
  destination_port: number;
  bookings_type: number | null;
  tariff_bracket: number | null;
  created_at?: string;
  transport_type_data: {
    id: number;
    name: string;
  };
  origin_port_data: {
    id: number;
    name: string;
    country: string | null;
  };
  destination_port_data: {
    id: number;
    name: string;
    country: string | null;
  };
  bookings_type_data: {
    id: number;
    name: string;
  } | null;
  tariff_bracket_data: {
    id: number;
    from: number | null;
    to: number | null;
  } | null;
  container_size_data: {
    id: number;
    name: string;
  } | null;
}

export const useTariffs = () => {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchTariffs = async () => {
      try {
        if (mounted) {
          setLoading(true);
        }

        const { data: response, error } = await supabase
          .from("tariffs")
          .select(`
            *,
            transport_type_data:booking_transport_types(*),
            origin_port_data:booking_origin_port(*),
            destination_port_data:booking_destination_port(*),
            tariff_bracket_data:tariff_brackets(*),
            container_size_data:container_size(*),
            bookings_type_data:bookings_type(*)
          `)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Tariffs query error:', error);
          throw error;
        }

        console.log('Tariffs query response:', response ? 'Data received' : 'No data', 
                   'Count:', Array.isArray(response) ? response.length : 0);

        if (mounted) {
          if (!response || !Array.isArray(response)) {
            console.error('Unexpected tariffs response structure:', response);
            setTariffs([]);
            setError('Unexpected data format received');
            setLoading(false);
            return;
          }

          const formattedTariffs: Tariff[] = (response as unknown as RawTariffResponse[])
            .filter(item => item.transport_type_data && item.origin_port_data && item.destination_port_data)
            .map((item) => ({
              id: item.id,
              from: item.from,
              to: item.to,
              transport_type: {
                id: item.transport_type_data.id,
                name: item.transport_type_data.name,
              },
              price: item.price,
              origin_port: {
                id: item.origin_port_data.id,
                name: item.origin_port_data.name,
                country: item.origin_port_data.country,
              },
              destination_port: {
                id: item.destination_port_data.id,
                name: item.destination_port_data.name,
                country: item.destination_port_data.country,
              },
              bookings_type: item.bookings_type_data,
              tariff_bracket: item.tariff_bracket_data,
              container_size: item.container_size_data,
            }));

          setTariffs(formattedTariffs);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch tariffs');
          setLoading(false);
        }
      }
    };

    // Initial fetch - always fetch tariffs regardless of auth status
    fetchTariffs();

    // Cleanup
    return () => {
      mounted = false;
    };
  }, []);

  return { tariffs, loading, error };
};